import { EyeOff, MoreHorizontal, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { HintedHead } from "~/components/ui/hint-tooltip";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { EVAL_SCORE_MAX, type PartialEvalCase } from "~/services/evals";
import {
  ADMIN_HEAD_CLS,
  CaseNameCell,
  EVAL_CELL_CLS,
  EVAL_HEAD_CLS,
  type EvalTableVariant,
  rowActivateProps,
  SortButton,
} from "./EvalTable";
import type { TableSort } from "./TableSort";
import {
  CASE_OUTCOME_CONFIG,
  CaseOutcome,
  FLAKINESS_FLAKY,
  FLAKINESS_VERY_FLAKY,
  NO_SCORE_CELL_CLASS,
  SCORE_BAND_CONFIG,
  SCORE_BAND_ORDER,
} from "./constants/scoring";
import { CaseSortKey } from "./constants/sorting";
import { OutcomeGlyph } from "./primitives/badges";
import { DatasetNames, type DatasetRefs, TagChips } from "./primitives/chips";
import { formatEvalTimestamp } from "./primitives/format";
import type { CaseHealthCell, CaseHealthRow } from "./selectors/cases";
import { parseApiTimestamp, scoreBandFor } from "./selectors/core";

const headClassFor = (variant: EvalTableVariant) =>
  variant === "admin" ? ADMIN_HEAD_CLS : EVAL_HEAD_CLS;

/** Column width and gap, shared by the header row and every cell row. */
const CELL_SIZE = "size-6";

/** Outcomes whose shape the legend has to explain; `passed` is the unmarked one. */
const GLYPH_LEGEND_ORDER: CaseOutcome[] = [
  CaseOutcome.MIXED,
  CaseOutcome.FAILED,
  CaseOutcome.ERRORED,
];

interface CaseHealthMatrixProps {
  /** Already sorted; the header only reports and sets the order. */
  rows: CaseHealthRow[];
  /** Catalog datasets by id, so a case names its dataset rather than showing a uuid. */
  datasets: DatasetRefs;
  sort: TableSort<CaseSortKey>;
  /** Recent runs the caller cannot read, which are absent from every row. */
  hiddenRunCount: number;
  /** Table styling; the dashboard's compact set unless a page asks for another. */
  variant?: EvalTableVariant;
  onSelectCase: (caseId: string) => void;
  onSelectCell: (row: CaseHealthRow, cell: CaseHealthCell) => void;
  onDeleteCase: (evalCase: PartialEvalCase) => void;
}

/**
 * Cases down, each case's own recent runs across, coloured by score.
 *
 * Every row is windowed over the runs that exercised *that* case, so it holds no
 * empty cells; a fixed column per run measured 36% blank, because runs are
 * tag-scoped. What that costs is the vertical read — only the newest cell of each
 * row lines up — so which run a cell is stays in its tooltip and accessible name.
 *
 * Two independent channels: fill is the case's mean score, shape is its outcome.
 * The suites assert on tool-call counts as well as on the answer, so a case can
 * score 3.4 and still fail one, and only a second channel shows both at once.
 *
 * Nothing here is reachable only by colour or only by hover.
 */
export const CaseHealthMatrix = ({
  rows,
  datasets,
  sort,
  hiddenRunCount,
  variant = "dashboard",
  onSelectCase,
  onSelectCell,
  onDeleteCase,
}: CaseHealthMatrixProps) => (
  <div>
    {hiddenRunCount > 0 && (
      // Stated rather than left to be inferred: a run the caller cannot read is
      // simply absent from every row, and a history missing its newest runs is
      // otherwise indistinguishable from a suite nobody has run lately.
      <p className="mb-3 flex items-center gap-1.5 rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
        <EyeOff className="size-3.5 shrink-0" aria-hidden />
        {hiddenRunCount} recent {hiddenRunCount === 1 ? "run hides its" : "runs hide their"} results
        from you and {hiddenRunCount === 1 ? "is" : "are"} missing from every row. You need read
        access to every dataset and agent a run touched.
      </p>
    )}
    <div className={cn(variant === "admin" && "rounded-lg border")}>
      <Table className="min-w-[900px] border-separate border-spacing-0">
        <TableCaption className="sr-only">
          Eval case health: one row per case, showing that case&apos;s mean judge score and outcome
          in each of its own most recent runs, oldest first. The runs differ from row to row, so a
          cell names its run rather than taking it from a column heading.
        </TableCaption>
        <TableHeader>
          <TableRow>
            <SortHeader
              variant={variant}
              columnKey={CaseSortKey.NAME}
              label="Case"
              sort={sort}
              isSticky
            />
            {/* The two facets the filter row narrows by, so a reviewer can see what
              a filter matched. Datasets are not sortable: a case's datasets have
              no single value to order by. */}
            <SortHeader
              variant={variant}
              columnKey={CaseSortKey.SUITE}
              label="Tags"
              sort={sort}
              align="left"
            />
            <TableHead scope="col" className={cn(headClassFor(variant), "text-left")}>
              Dataset
            </TableHead>
            <SortHeader
              variant={variant}
              columnKey={CaseSortKey.PASS_RATE}
              label="Passed"
              sort={sort}
              hint="An iteration passes when every assertion holds and, where the case sets a pass threshold, every judge score meets it. A case with no threshold is decided by its assertions alone."
            />
            <SortHeader
              variant={variant}
              columnKey={CaseSortKey.FLAKINESS}
              label="Flakiness"
              sort={sort}
            />
            {/* Against the right edge, so every row's newest run — the one a
              reviewer is here to read — lands in the same place. It is the one
              column that does line up: leftward, each row reaches back through
              its own history and a row runs out where its case does. Said here
              because a heading over ragged rows would otherwise be read as one. */}
            <TableHead scope="col" className={cn(headClassFor(variant), "text-right")}>
              <span className="whitespace-nowrap">
                Latest runs for this case,{" "}
                <span className="font-normal text-muted-foreground/70">oldest → newest</span>
              </span>
            </TableHead>
            <TableHead scope="col" className={cn(headClassFor(variant), "w-8")}>
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.evalCase.id}
              {...rowActivateProps(
                () => onSelectCase(row.evalCase.id),
                "group [tbody_&]:hover:bg-transparent"
              )}
            >
              <th
                scope="row"
                className={cn(
                  EVAL_CELL_CLS,
                  "sticky left-0 z-10 max-w-[320px] border-t border-border bg-card text-left font-normal group-hover:bg-muted/40"
                )}
              >
                <CaseNameCell
                  name={row.evalCase.name}
                  description={row.evalCase.description}
                  onSelect={() => onSelectCase(row.evalCase.id)}
                  underlineOnHover={false}
                />
              </th>
              <TableCell
                className={cn(
                  EVAL_CELL_CLS,
                  "max-w-[180px] border-t border-border text-left group-hover:bg-muted/40"
                )}
              >
                <TagChips tags={row.evalCase.tags} max={2} />
              </TableCell>
              <TableCell
                className={cn(
                  EVAL_CELL_CLS,
                  "max-w-[180px] border-t border-border text-left group-hover:bg-muted/40"
                )}
              >
                <DatasetNames
                  ids={row.evalCase.dataset_ids}
                  datasets={datasets}
                  className="text-xs"
                  max={1}
                />
              </TableCell>
              {/* A pass rate rather than a meter: it answers a narrower question
                than the score — how often the case cleared its threshold — and
                a second bar competing with the grid was why the score was hard
                to find. Shown as a percent to read as an average like the score
                beside it, pooled over the same iterations; the raw count is on
                hover. */}
              <TableCell
                className={cn(
                  EVAL_CELL_CLS,
                  "border-t border-border text-right group-hover:bg-muted/40"
                )}
              >
                {row.passRate != null ? (
                  <span
                    className="text-xs tabular-nums text-muted-foreground"
                    title={`${row.passedCount} of ${row.judgedCount} iterations passed`}
                  >
                    {Math.round(row.passRate * 100)}%
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell
                className={cn(
                  EVAL_CELL_CLS,
                  "border-t border-border text-right group-hover:bg-muted/40"
                )}
              >
                <FlakinessValue flakiness={row.flakiness} />
              </TableCell>
              <TableCell
                className={cn(EVAL_CELL_CLS, "border-t border-border group-hover:bg-muted/40")}
              >
                <div className="flex items-center justify-end gap-1">
                  {row.cells.length === 0 ? (
                    // A case no readable run has exercised, which is a finding —
                    // an empty strip would read as a rendering gap. Italic as well
                    // as muted, so it does not read as one more value.
                    <span className="text-xs text-muted-foreground italic">Not run yet</span>
                  ) : (
                    row.cells.map((cell) => (
                      <ScoreCell
                        key={cell.runId}
                        cell={cell}
                        caseName={row.evalCase.name}
                        onSelect={() => onSelectCell(row, cell)}
                      />
                    ))
                  )}
                </div>
              </TableCell>
              <TableCell
                className={cn(EVAL_CELL_CLS, "border-t border-border group-hover:bg-muted/40")}
              >
                {/* Stops the click reaching the row, which would open the case. */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                      aria-label={`Actions for ${row.evalCase.name}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <MoreHorizontal className="size-4" aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDeleteCase(row.evalCase);
                      }}
                    >
                      <Trash2 className="size-4" aria-hidden />
                      Delete case
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>

    <MatrixLegend />
  </div>
);

/**
 * One case in one run: filled by score, marked by outcome. A cell with no score —
 * every iteration errored — is hollow rather than given a fifth fill, since
 * tinting an absence makes it compete with the scale.
 *
 * The run comes off the cell because there are no columns to take it from, which
 * makes the timestamp load-bearing rather than decorative.
 */
const ScoreCell = ({
  cell,
  caseName,
  onSelect,
}: {
  cell: CaseHealthCell;
  caseName: string;
  onSelect: () => void;
}) => {
  const config = CASE_OUTCOME_CONFIG[cell.outcome];
  const band = scoreBandFor(cell.meanScore);
  const isScored = cell.meanScore != null && band != null;
  const fillClass = band
    ? SCORE_BAND_CONFIG[band].cellClass
    : NO_SCORE_CELL_CLASS[CaseOutcome.ERRORED];
  // The dashboard's own timestamp format, so one run reads the same in the
  // grid, in the runs table and on its own page.
  const startedAt = cell.run ? formatEvalTimestamp(parseApiTimestamp(cell.run.started_at)) : "";
  const scoreText = cell.meanScore != null ? `scored ${cell.meanScore.toFixed(2)}` : "no score";

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onSelect}
          className="cursor-pointer rounded-[3px] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          aria-label={`${caseName}: ${scoreText}, ${config.label}, in the run started ${startedAt}`}
        >
          <div
            className={cn(
              CELL_SIZE,
              "flex items-center justify-center rounded-[3px] transition-transform hover:scale-110",
              fillClass
            )}
          >
            <OutcomeGlyph outcome={cell.outcome} />
          </div>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        {/* The case is the row the pointer is already on, so naming it here only
            repeats it. What a 24px square cannot say for itself is the reading,
            the outcome behind it, and which run it was. */}
        <p className="text-xs font-medium">
          {isScored
            ? `${cell.meanScore?.toFixed(2)} / ${EVAL_SCORE_MAX} · ${SCORE_BAND_CONFIG[band].label}`
            : config.label}
        </p>
        {cell.judgedCount > 0 && (
          <p className="text-xs">
            {/* The outcome leads this line only where the line above gave a
                score; unscored, that line is already the outcome. */}
            {isScored && `${config.label} · `}
            {cell.passedCount} of {cell.judgedCount}{" "}
            {cell.judgedCount === 1 ? "iteration" : "iterations"} passed
          </p>
        )}
        {/* Which run this is. Secondary ink on the tooltip's inverted surface is
            a transparency of its own text colour — `text-muted-foreground` is
            mixed for the page background and reads as barely there on this one. */}
        {startedAt && <p className="text-background/70 text-xs">{startedAt}</p>}
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * Flakiness as a word, not a bare ratio nobody has a scale for. Below
 * `FLAKINESS_FLAKY` the case reads "Stable" rather than blank — a small wobble is
 * a finding of stability. The measured movement stays in the title attribute.
 */
const FlakinessValue = ({ flakiness }: { flakiness: number | null }) => {
  if (flakiness == null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const title = `Pass rate moves ${Math.round(flakiness * 100)} points per run, on average`;
  if (flakiness < FLAKINESS_FLAKY) {
    return (
      <span className="text-xs text-muted-foreground" title={title}>
        Stable
      </span>
    );
  }
  const isSevere = flakiness >= FLAKINESS_VERY_FLAKY;
  return (
    <span
      className={cn("text-xs font-medium", isSevere ? "text-red-700" : "text-orange-700")}
      title={title}
    >
      {isSevere ? "Very flaky" : "Flaky"}
    </span>
  );
};

/**
 * Two legends, because the grid has two channels and one strip would imply the
 * marks are alternatives. The ramp wears the judges' own rubric words.
 */
const MatrixLegend = () => (
  <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-border pt-3">
    <span className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-foreground">Score</span>
      {SCORE_BAND_ORDER.map((band) => (
        <span key={band} className="flex items-center gap-1 text-xs text-muted-foreground">
          <span
            className={cn("size-3.5 rounded-[3px]", SCORE_BAND_CONFIG[band].swatchClass)}
            aria-hidden
          />
          {SCORE_BAND_CONFIG[band].label}
        </span>
      ))}
    </span>

    <span className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-foreground">Outcome</span>
      {GLYPH_LEGEND_ORDER.map((outcome) => (
        <span key={outcome} className="flex items-center gap-1 text-xs text-muted-foreground">
          <span
            className={cn(
              "flex size-3.5 items-center justify-center rounded-[3px]",
              outcome === CaseOutcome.ERRORED
                ? NO_SCORE_CELL_CLASS[CaseOutcome.ERRORED]
                : "text-foreground ring-1 ring-inset ring-border"
            )}
            aria-hidden
          >
            <OutcomeGlyph outcome={outcome} className="size-2.5" />
          </span>
          {CASE_OUTCOME_CONFIG[outcome].label}
        </span>
      ))}
    </span>
  </div>
);

/**
 * A sortable header for the grid's leading columns. The grid is hand-built rather
 * than a shadcn `Table`, so it borrows the sort button and skips `TableHead`.
 */
const SortHeader = ({
  columnKey,
  label,
  sort,
  isSticky = false,
  align = "right",
  hint,
  variant,
}: {
  variant: EvalTableVariant;
  columnKey: CaseSortKey;
  label: string;
  sort: TableSort<CaseSortKey>;
  isSticky?: boolean;
  /** Which edge the column's content sits against; a sticky column is always left. */
  align?: "left" | "right";
  /** A rule the column's numbers cannot show, offered on hover. */
  hint?: string;
}) => {
  const isLeftAligned = isSticky || align === "left";

  return (
    <TableHead
      scope="col"
      aria-sort={columnKey === sort.sortKey ? `${sort.direction}ending` : "none"}
      className={cn(
        headClassFor(variant),
        isSticky && "sticky left-0 z-10 bg-card",
        isLeftAligned ? "text-left" : "text-right"
      )}
    >
      <HintedHead label={label} hint={hint}>
        <SortButton
          columnKey={columnKey}
          label={label}
          sort={sort}
          alignRight={!isLeftAligned}
          variant={variant}
        />
      </HintedHead>
    </TableHead>
  );
};
